import React from 'react';
import { connect } from 'react-redux';
import { Button, Card, CardBody, Col, FormGroup } from 'reactstrap';
import ReduxFormInput from '@Utils/ReduxFormInput';
import validate, { ClubParams } from './club.new.validate';
import { AppState } from '@Redux/store';
import { Dispatch } from '@Redux/type';
import { FormattedMessage } from 'react-intl';
import { defaultMessages } from '@I18n/default';
import { Club } from '@Models';
import { Field, Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import ReduxFormSelect from '@Utils/ReduxFormSelect';
import { container } from '@Container';
import { TYPES } from '@Types';
import { CategoriesAction } from '@Redux/actions/federation/categories.action';

interface Props {
  club: Club,
  cancel: () => void,
  onSubmit: (values: ClubParams) => void,
  canSubmit: boolean,
}

const ClubForm: React.FC<FormProps> = (
  {
    load,
    club,
    categories,
    cancel,
    onSubmit
  }
) => {
  React.useEffect(() => {
    load();
  }, []);

  return (
    <Form
      initialValues={{
        name: club.name,
        location: club.location,
        country: club.country,
        notes: club.notes,
        contact: club.contact,
        members: club.members,
        meets: club.meets,
        websites: club.websites,
        categories: club.categories
      }}
      onSubmit={onSubmit}
      validate={validate}
      subscription={{ submitting: true, pristine: true }}
      mutators={{ ...arrayMutators }}
    >
      {({ handleSubmit, form: { mutators: { push, remove } }, submitting, pristine }) => (
        <form onSubmit={handleSubmit}>
          <Col sm="12">
            <Card className="card-border">
              <CardBody>
                <h2 className="text-center"><FormattedMessage {...defaultMessages.federationClubNewTitle} /></h2>

                <FormGroup row={true}>
                  <Col xs="12">
                    <Field
                      name="name"
                      type="text"
                      component={ReduxFormInput}
                      label={defaultMessages.federationClubFormNameTitle}
                      placeHolder={defaultMessages.federationClubFormNamePlaceholder}
                    />
                  </Col>
                  <Col xs="12">
                    <Field
                      name="country"
                      type="text"
                      component={ReduxFormInput}
                      label={defaultMessages.federationClubFormCountryTitle}
                      placeHolder={defaultMessages.federationClubFormCountryPlaceholder}
                    />
                  </Col>
                  <Col xs="12">
                    <Field
                      name="location"
                      type="text"
                      component={ReduxFormInput}
                      label={defaultMessages.federationClubFormLocationTitle}
                      placeHolder={defaultMessages.federationClubFormLocationPlaceholder}
                    />
                  </Col>
                  <Col xs="12">
                    <Field
                      name="notes"
                      type="text"
                      component={ReduxFormInput}
                      label={defaultMessages.federationClubFormNotesTitle}
                      placeHolder={defaultMessages.federationClubFormNotesPlaceholder}
                    />
                  </Col>
                  <Col xs="12">
                    <Field
                      name="contact"
                      type="text"
                      component={ReduxFormInput}
                      label={defaultMessages.federationClubFormContactTitle}
                      placeHolder={defaultMessages.federationClubFormContactPlaceholder}
                    />
                  </Col>
                  <Col xs="12">
                    <Field
                      name="members"
                      type="text"
                      component={ReduxFormInput}
                      label={defaultMessages.federationClubFormMembersTitle}
                      placeHolder={defaultMessages.federationClubFormMembersPlaceholder}
                    />
                  </Col>
                  <Col xs="12">
                    <Field
                      name="meets"
                      type="text"
                      component={ReduxFormInput}
                      label={defaultMessages.federationClubFormMeetsTitle}
                      placeHolder={defaultMessages.federationClubFormMeetsPlaceholder}
                    />
                  </Col>
                  <Col xs={12}>
                    <FieldArray name="websites">
                      {({ fields }) =>
                        fields.map((name, index) => (
                          <div className="d-flex" key={name}>
                            <Field
                              className="flex-grow-1"
                              name={`${name}.link`}
                              type="text"
                              component={ReduxFormInput}
                              label={defaultMessages.federationClubFormWebsiteTitle}
                              placeHolder={defaultMessages.federationClubFormWebsitePlaceholder}
                            />
                            <Button color="age" type="button" onClick={() => remove('websites', index)}
                                    className="p-1 m-1"><i className="fa-solid fa-2x fa-trash"></i></Button>
                          </div>
                        ))
                      }
                    </FieldArray>
                    <Button color="age" type="button" onClick={() => push('websites', undefined)}>Add Website</Button>
                  </Col>
                  <Col xs={12}>
                    <FieldArray name="categories">
                      {({ fields }) =>
                        fields.map((name, index) => (
                          <div className="d-flex" key={name}>
                            <Field
                              className="flex-grow-1"
                              name={`${name}.categoryId`}
                              type="text"
                              datas={categories}
                              component={ReduxFormSelect}
                              label={defaultMessages.federationClubFormCategoryTitle}
                              placeHolder={defaultMessages.federationClubFormCategoryPlaceholder}
                            />
                            <Button color="age" type="button" onClick={() => remove('categories', index)}
                                    className="p-1 m-1"><i className="fa-solid fa-2x fa-trash"></i></Button>
                          </div>
                        ))
                      }
                    </FieldArray>
                    <Button color="age" type="button" onClick={() => push('categories', undefined)}>Add Game</Button>
                  </Col>
                </FormGroup>
              </CardBody>
              <div className="mb-2 text-center">
                <Button disabled={submitting} color="age" type="button" onClick={cancel}>{
                  <FormattedMessage {...defaultMessages.formCancel} />}</Button>
                <Button disabled={submitting} color="age" type="submit">{
                  <FormattedMessage {...defaultMessages.formSave} />}</Button>
              </div>
            </Card>
          </Col>
        </form>
      )}
    </Form>
  );
};

const mapStateToProps = (state: AppState, props: Props) => (
  {
    onSubmit: props.onSubmit,
    club: props.club,
    categories: state.federationCategories.entity ?? []
  }
);

const mapDispatchToProps = (dispatch: Dispatch) => {
  const categoriesAction = container.get<CategoriesAction>(TYPES.Actions.Federation.CategoriesAction);

  return {
    load: () => dispatch(categoriesAction.fetchData())
  };
};

type FormProps = Props &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>

export default connect(mapStateToProps, mapDispatchToProps)(ClubForm);
